import { useEffect, useState } from 'react';

import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import Display from 'shared/components/Display';
import InputForm from 'shared/components/Input/InputForm';
import Loader from 'shared/containers/Loader';
import { APIErrorT } from 'shared/models/api';
import { LicenceT } from 'shared/models/general-system';
import api from 'utils/api';

import './InstanceSettings.scss';

import validator from './InstanceSettings.validator';

const TOKEN_IS_EXPIRED = -1;
const TOKEN_INVALID = -2;

type FormValues = {
  token: string;
};
function InstanceSettings() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deviceLimit, setDeviceLimit] = useState(0);
  const [expiry, setExpiry] = useState(0);
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: {
      errors,
      isDirty,
    },
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: validator,
  });
  const registredToken = register('token');

  const manageNewToken = (token: string, isBackend?: boolean) => {
    try {
      const decodedToken: LicenceT = jwtDecode(token);
      const unixExpiry = decodedToken.exp;
      setDeviceLimit(decodedToken.DeviceLimit);
      if (decodedToken.DeviceLimit === undefined || decodedToken.exp === undefined) {
        setDeviceLimit(TOKEN_INVALID);
        setExpiry(TOKEN_INVALID);
        setError('token', { message: 'Token doesn\'t contain the expected informations' });
      } else if (moment.unix(unixExpiry).isBefore(moment.now())) {
        setExpiry(TOKEN_IS_EXPIRED);
        toast.error('Token is expired');
        setError('token', { message: 'Token is expired' });
      } else {
        setExpiry(unixExpiry);
      }
    } catch {
      if (isBackend) {
        toast.warn('Token from backend is invalid');
      }
      setDeviceLimit(TOKEN_INVALID);
      setExpiry(TOKEN_INVALID);
    }
  };

  useEffect(() => {
    api.general.getLicenseToken()
      .then((resp: Response) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              setIsLoading(false);
            });
        } else {
          resp.json()
            .then((data: any) => {
              const { token } = data;
              manageNewToken(token);
              setValue('token', token);
              setIsLoading(false);
            });
        }
      });
  }, []);

  const onSubmit: SubmitHandler<FormValues> = (formData: FormValues) => {
    api.general.updateLicenseToken(formData.token)
      .then((resp: Response) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              setIsSubmitting(false);
              let message = error?.message;
              if (message === 'invalid authentication token') {
                setError('token', { message: 'Invalid licence token' });
                message = 'Failed to save the license token';
              }
              toast.error(message);
            });
        } else {
          resp.json()
            .then(() => {
              toast.success('Successfully updated the license token');
              setIsSubmitting(false);
            });
        }
      });
  };

  const onChangeToken = (event: any) => {
    manageNewToken(event?.target?.value);
    registredToken.onBlur(event);
  };

  const tokenIsExpired = () => expiry === TOKEN_IS_EXPIRED;
  const tokenIsInvalid = () => deviceLimit === TOKEN_INVALID || expiry === TOKEN_INVALID;

  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <form className="setting-component" onSubmit={handleSubmit(onSubmit)}>
      <div className="title__header">
        <h1>Instance Settings</h1>
        <ButtonSubmitData
          isEdit
          isSubmitting={isSubmitting}
          disabled={!isDirty || tokenIsExpired()}
        />
      </div>
      <div className="form__section" style={{ marginTop: '.5rem' }}>
        <h3>Device limit token</h3>
        <h4>
          The device token limit is a JSON web token provided by an administrator
          to limit the number of devices allowed within the current instance.
        </h4>
        <InputForm.TextField
          fullWidth
          sx={{ mt: 1 }}
          multiline
          maxRows={4}
          label="License Token"
          register={registredToken}
          errorField={errors.token}
          onBlur={onChangeToken}
        />
        <Display.CardWithIcon
          name="Device Limit"
          value={tokenIsInvalid() ? '' : deviceLimit}
          icon={<SettingsRemoteIcon color="primary" />}
          disablePadding={false}
          disabled={tokenIsInvalid()}
        />
        <Display.CardWithIcon
          name="Expiry"
          value={tokenIsInvalid() ? '' : moment.unix(expiry).fromNow()}
          icon={<HourglassBottomIcon color={!tokenIsExpired() ? 'primary' : 'error'} />}
          disablePadding={false}
          disabled={tokenIsInvalid()}
          titleColor={tokenIsExpired() ? 'error' : undefined}
        />
      </div>
    </form>
  );
}

export default InstanceSettings;
